import React, { useMemo } from 'react';
import styled, { keyframes, css } from 'styled-components';

/* ─── Particle animation paths ─── */

const fall = keyframes`
  0% { transform: translateY(-20px) rotate(0deg); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(110%) rotate(360deg); opacity: 0; }
`;

const float = keyframes`
  0% { transform: translateY(0) translateX(0) scale(0.8); opacity: 0; }
  15% { opacity: 1; }
  50% { transform: translateY(-30px) translateX(15px) scale(1); }
  85% { opacity: 1; }
  100% { transform: translateY(-60px) translateX(-10px) scale(0.6); opacity: 0; }
`;

const rise = keyframes`
  0% { transform: translateY(20px) scale(0.5); opacity: 0; }
  20% { opacity: 1; }
  80% { opacity: 0.8; }
  100% { transform: translateY(-120%) scale(1.2); opacity: 0; }
`;

const drift = keyframes`
  0% { transform: translateY(-10px) translateX(-20px) rotate(0deg); opacity: 0; }
  15% { opacity: 1; }
  50% { transform: translateY(40px) translateX(10px) rotate(180deg); }
  85% { opacity: 0.8; }
  100% { transform: translateY(100%) translateX(-15px) rotate(360deg); opacity: 0; }
`;

const ANIMATIONS = {
  'cherry-blossoms': drift,
  lanterns: rise,
  snowflakes: fall,
  leaves: drift,
  stars: float,
};

/* ─── Styled particle ─── */

const ParticleContainer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
`;

const Particle = styled.div`
  position: absolute;
  font-size: ${props => props.$size || '16px'};
  left: ${props => props.$left};
  top: ${props => props.$top};
  ${props => css`
    animation: ${ANIMATIONS[props.$animType] || float} ${props.$duration} ease-in-out ${props.$delay} infinite;
  `}
  will-change: transform, opacity;
  opacity: 0;
`;

/* ─── Component ─── */

const ThemedParticles = ({ theme, count = 12 }) => {
  const particles = useMemo(() => {
    const { particleEmojis, particleType } = theme;
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      emoji: particleEmojis[i % particleEmojis.length],
      left: `${5 + Math.random() * 90}%`,
      top: particleType === 'lanterns' || particleType === 'stars'
        ? `${60 + Math.random() * 30}%`
        : `${-5 + Math.random() * 20}%`,
      size: `${14 + Math.random() * 10}px`,
      duration: `${2.5 + Math.random() * 2}s`,
      delay: `${i * 0.18}s`,
    }));
  }, [theme, count]);

  return (
    <ParticleContainer>
      {particles.map(p => (
        <Particle
          key={p.id}
          $animType={theme.particleType}
          $left={p.left}
          $top={p.top}
          $size={p.size}
          $duration={p.duration}
          $delay={p.delay}
        >
          {p.emoji}
        </Particle>
      ))}
    </ParticleContainer>
  );
};

export default ThemedParticles;
